import { useCallback, useRef, useState } from 'react';

import { useBeforeUnload, useBlocker } from '@shared/hooks';

export const useAdvancedSettingsUnsavedGuard = ({
  isDirty,
  onClose,
  onSave,
}: {
  isDirty: boolean;
  onClose: () => void;
  onSave: () => Promise<boolean>;
}) => {
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [isUnsavedChangesSaving, setIsUnsavedChangesSaving] = useState(false);
  const routerNavigationResolveRef = useRef<((proceed: boolean) => void) | null>(null);

  const resolveRouterNavigation = useCallback((proceed: boolean) => {
    setShowUnsavedChangesDialog(false);
    routerNavigationResolveRef.current?.(proceed);
    routerNavigationResolveRef.current = null;
  }, []);

  const requestRouterNavigationConfirmation = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      routerNavigationResolveRef.current = resolve;
      setShowUnsavedChangesDialog(true);
    });
  }, []);

  const requestClose = useCallback(() => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
      return;
    }

    onClose();
  }, [isDirty, onClose]);

  const handleCancelUnsavedChanges = useCallback(() => {
    resolveRouterNavigation(false);
  }, [resolveRouterNavigation]);

  const handleDiscardUnsavedChanges = useCallback(() => {
    if (routerNavigationResolveRef.current) {
      resolveRouterNavigation(true);
      return;
    }

    setShowUnsavedChangesDialog(false);
    onClose();
  }, [onClose, resolveRouterNavigation]);

  const handleSaveFromUnsavedChangesDialog = useCallback(async () => {
    if (isUnsavedChangesSaving) return;

    setIsUnsavedChangesSaving(true);
    try {
      const saved = await onSave();
      if (!saved) return;

      if (routerNavigationResolveRef.current) {
        resolveRouterNavigation(true);
        return;
      }

      setShowUnsavedChangesDialog(false);
      onClose();
    } finally {
      setIsUnsavedChangesSaving(false);
    }
  }, [isUnsavedChangesSaving, onClose, onSave, resolveRouterNavigation]);

  // This hook is the sole owner of both browser/history side effects for this modal.
  useBeforeUnload(isDirty);
  useBlocker(isDirty, requestRouterNavigationConfirmation);

  return {
    showUnsavedChangesDialog,
    isUnsavedChangesSaving,
    requestClose,
    handleCancelUnsavedChanges,
    handleDiscardUnsavedChanges,
    handleSaveFromUnsavedChangesDialog,
  };
};
